import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// More human, conversational translations
const translations = {
  en: {
    // Navigation
    nav: {
      howItWorks: "How it works",
      pricing: "Pricing",
      faq: "FAQ",
      login: "Sign in",
      getStarted: "Get started",
      dashboard: "Dashboard",
      profile: "Profile",
      documents: "Documents",
      logout: "Log out"
    },
    // Hero - First 30 days + mistake prevention
    hero: {
      headline: "Everything you need to settle in Canada in your first 30 days in the right order.",
      subheadline: "Step-by-step guidance and document checks to avoid mistakes, delays, and confusion.",
      cta: "Get my step-by-step plan",
      ctaHint: "Full plan access — free preview available",
      tagline: "Avoid mistakes. Submit with confidence."
    },
    // Trust signals
    trust: {
      helped: "documents reviewed",
      rating: "submission confidence",
      bilingual: "Fully bilingual"
    },
    // Problem section - More empathetic
    problem: {
      title: "One small mistake can delay everything by weeks",
      subtitle: "By the time you notice something is wrong, it's already too late.",
      items: [
        "\"My application got rejected for a missing signature...\"",
        "\"I waited 3 weeks only to find out I used the wrong form...\"",
        "\"I wish someone had checked my documents before I submitted.\""
      ]
    },
    // Common mistakes section
    commonMistakes: {
      wrongTitle: "Most newcomers get this wrong",
      wrongItems: [
        "Opening a bank account before getting a SIN",
        "Missing required documents → delays",
        "Doing steps in the wrong order",
        "Spending hours searching unclear information"
      ],
      result: "Result: weeks lost, unnecessary delays, and avoidable frustration.",
      rightTitle: "How PaperPal helps you avoid these mistakes",
      rightSubtitle: "Follow the right steps from day one. No confusion, no delays.",
      rightItems: [
        "Clear order (no guesswork)",
        "Exact documents needed",
        "Step-by-step guidance",
        "Everything in one place"
      ]
    },
    // Solution section
    solution: {
      title: "Submit with confidence",
      subtitle: "Know exactly what's missing before you hit send",
      description: "Upload your documents and get instant feedback on what's complete, what needs fixing, and whether you're ready to submit."
    },
    // How it works
    howItWorks: {
      title: "Here's how it works",
      subtitle: "Three steps to submission confidence",
      steps: [
        { 
          title: "Tell us about yourself", 
          description: "Student? Worker? Just arrived? We'll ask a few simple questions.",
          details: [
            "Tell us your status (student, worker, newcomer)",
            "Share when you arrived in Canada",
            "Let us know your province",
            "Describe your biggest concerns"
          ]
        },
        { 
          title: "Upload your documents", 
          description: "Share your forms, applications, or IDs — we'll review them instantly.",
          details: [
            "Supports PDFs, images, and scanned documents",
            "AI-powered analysis for completeness",
            "Identifies missing fields and common errors",
            "Works with SIN, bank, and immigration forms"
          ]
        },
        { 
          title: "Know exactly if you're ready to submit", 
          description: "See exactly what's ready, what needs fixing, and your submission readiness.",
          details: [
            "Clear ready/not ready status",
            "Specific issues with explanations",
            "What looks good confirmation",
            "Next steps after submission"
          ]
        }
      ]
    },
    // What's included
    included: {
      title: "Everything you need to submit right the first time",
      subtitle: "Avoid delays and rejections",
      items: [
        { title: "Document completeness check", description: "We scan for missing fields, signatures, and dates" },
        { title: "Submission confidence score", description: "Know if you're ready before you submit" },
        { title: "Specific fix recommendations", description: "Clear instructions on what needs attention" },
        { title: "Common mistake detection", description: "We catch errors others miss" },
        { title: "Processing time estimates", description: "Know what to expect after submission" },
        { title: "Full bilingual support", description: "Everything available in English and French" }
      ]
    },
    // Pricing - More conversational
    pricing: {
      title: "One price. Everything included.",
      subtitle: "No subscriptions, no hidden fees",
      planName: "Your First 30 Days",
      price: "$49",
      refPrice: "$99",
      priceNote: "today",
      referralNote: "Pay only $39 with a referral",
      earlyAccess: "Early access pricing — increasing soon",
      oneTime: "One-time payment — instant access",
      features: [
        "Personalized 30-day checklist",
        "Step-by-step guidance in EN/FR",
        "Document completeness review",
        "Common mistakes to avoid",
        "Email support when you need it"
      ],
      cta: "Get my step-by-step plan",
      guarantee: "Not helpful? We'll make it right. No questions asked."
    },
    // Founder - More personal
    founder: {
      title: "Why I built this",
      message: "Hi, I'm Nicolas. When I helped friends navigate Canadian paperwork, I saw how confusing and stressful it was — even for smart, capable people. Government sites are a maze. Online advice is contradictory. And missing a step can cost you weeks.\n\nPaperPal gives you a clear path, so you don't waste time figuring it out alone."
    },
    // Testimonials
    testimonials: {
      title: "What early users are saying"
    },
    // FAQ
    faq: {
      title: "Questions? We've got answers.",
      viewAll: "See all questions"
    },
    // CTA
    cta: {
      title: "Ready to submit with confidence?",
      subtitle: "Catch mistakes before they cause delays.",
      button: "Get my step-by-step plan",
      belowCta: "Takes less than 2 minutes to get started"
    },
    // Footer
    footer: {
      tagline: "A clearer start to your first 30 days in Canada.",
      about: "About",
      contact: "Contact",
      disclaimer: "Disclaimer",
      privacy: "Privacy",
      terms: "Terms",
      disclaimerText: "PaperPal provides guidance and explanations for educational purposes. We're not lawyers, immigration consultants, or government representatives."
    },
    // Auth - Friendlier
    auth: {
      login: "Welcome back",
      signup: "Create your account",
      email: "Email",
      password: "Password",
      firstName: "First name",
      lastName: "Last name",
      confirmPassword: "Confirm password",
      forgotPassword: "Forgot password?",
      noAccount: "New here?",
      hasAccount: "Already have an account?",
      signupLink: "Create account",
      loginLink: "Log in",
      orContinueWith: "or",
      google: "Continue with Google"
    },
    // Onboarding - Calmer, more encouraging
    onboarding: {
      title: "Let's get you set up",
      step1: {
        title: "Which language do you prefer?",
        subtitle: "You can change this anytime.",
        options: ["English", "Français"]
      },
      step2: {
        title: "What brings you to Canada?",
        options: [
          { value: "international_student", label: "I'm here to study" },
          { value: "worker", label: "I'm here to work" },
          { value: "newcomer", label: "I'm still figuring it out" }
        ]
      },
      step3: {
        title: "When did you arrive?",
        subtitle: "This helps us prioritize your tasks.",
        options: [
          { value: "not_arrived", label: "I haven't arrived yet" },
          { value: "within_7_days", label: "Within the last week" },
          { value: "1_4_weeks", label: "1–4 weeks ago" },
          { value: "over_month", label: "More than a month ago" }
        ]
      },
      step4: {
        title: "Which province are you in?",
        subtitle: "Your plan is adapted to your province. Each province has different steps, timelines, and requirements. We adjust everything so you follow the right process from day one.",
        options: [
          { value: "ontario", label: "Ontario" },
          { value: "quebec", label: "Quebec" },
          { value: "other", label: "Another province" }
        ],
        otherNote: "We will still help. Some details may vary by province, and we will let you know when they do."
      },
      step5: {
        title: "Are you able to work in Canada?",
        subtitle: "This affects some paperwork priorities.",
        options: [
          { value: "yes", label: "Yes, I can work" },
          { value: "not_yet", label: "Not yet" },
          { value: "not_sure", label: "I'm not sure" }
        ]
      },
      step6: {
        title: "What's stressing you out most?",
        subtitle: "Pick as many as apply — we'll prioritize these.",
        options: [
          { value: "sin", label: "Getting my SIN" },
          { value: "bank", label: "Opening a bank account" },
          { value: "health", label: "Health insurance" },
          { value: "taxes", label: "Understanding taxes" },
          { value: "forms", label: "Work or school forms" },
          { value: "unknown", label: "I don't know where to start" }
        ]
      },
      step7: {
        title: "How can we help you best?",
        options: [
          { value: "instructions", label: "Give me clear step-by-step instructions" },
          { value: "explanations", label: "Just explain what things mean" },
          { value: "both", label: "Both, please!" }
        ]
      },
      step8: {
        title: "One quick note",
        notice: "PaperPal helps you understand what to do and in what order. It is based on real requirements across Canada and designed to help you avoid common mistakes. PaperPal provides guidance, not legal advice.",
        confirm: "Got it, let's go"
      },
      step9: {
        title: "Creating your checklist...",
        subtitle: "This only takes a moment."
      },
      next: "Continue",
      back: "Back",
      submit: "Create my checklist"
    },
    // Dashboard - Clearer hierarchy
    dashboard: {
      welcome: "Welcome back",
      greeting: "Here's what's next on your list.",
      yourChecklist: "Your First 30 Days",
      progress: "complete",
      nextStep: "Up next",
      sections: {
        day_1_3: "Week 1: The essentials",
        day_4_7: "Week 1: Money & health",
        day_8_14: "Week 2: Government setup",
        day_15_30: "Weeks 3–4: Work & school",
        after_arrival: "After your arrival"
      },
      status: {
        not_started: "To do",
        in_progress: "In progress",
        completed: "Done"
      },
      documents: {
        title: "Document check",
        description: "Upload a form and we'll review it for completeness.",
        upload: "Upload a document"
      },
      help: {
        title: "Need a hand?",
        description: "We're here if you get stuck.",
        contact: "Get in touch"
      }
    },
    // Checklist item
    checklistItem: {
      whatIs: "What is this?",
      whoNeeds: "Who needs to do this?",
      when: "When should I do this?",
      whatToPrepare: "What do I need to bring?",
      commonMistakes: "Common mistakes to avoid",
      nextStep: "What comes next?",
      notes: "Your notes",
      notesPlaceholder: "Jot down anything you want to remember...",
      markComplete: "Mark as done",
      markInProgress: "Mark as in progress",
      markNotStarted: "Mark as to do",
      back: "Back to checklist"
    },
    // Documents
    documents: {
      title: "Document review",
      subtitle: "We'll check your forms for completeness",
      upload: "Upload document",
      dragDrop: "Drop your file here, or click to browse",
      allowedTypes: "PDF, JPG, or PNG — up to 10MB",
      review: "Review this document",
      reviewing: "Reviewing...",
      status: {
        pending: "Waiting for review",
        reviewed: "Reviewed"
      },
      disclaimer: "We check for completeness, not legal validity. Always verify with official sources."
    },
    // Profile
    profile: {
      title: "Your profile",
      settings: "Preferences",
      language: "Language",
      save: "Save changes",
      saved: "Saved!"
    },
    // Contact
    contact: {
      title: "Get in touch",
      subtitle: "We usually respond within a day or two.",
      name: "Your name",
      email: "Email",
      message: "How can we help?",
      send: "Send message",
      sent: "Thanks! We'll be in touch soon."
    },
    // Legal
    legal: {
      title: "Important disclaimer",
      content: [
        "PaperPal provides general guidance and explanations for educational purposes.",
        "We are not a law firm, immigration consultancy, or government agency.",
        "We do not provide legal, immigration, or tax advice.",
        "The information we provide may not be complete, current, or accurate for your specific situation.",
        "Always verify information with official government sources or licensed professionals.",
        "By using PaperPal, you acknowledge these limitations."
      ]
    },
    // Common
    common: {
      loading: "Loading...",
      error: "Something went wrong",
      retry: "Try again",
      cancel: "Cancel",
      save: "Save",
      delete: "Delete",
      edit: "Edit",
      close: "Close"
    },
    // Premium / Freemium
    premium: {
      badge: "Premium",
      freeBadge: "Free",
      yourPlan: "Your Personal Plan",
      planDescription: "Prioritized steps based on your situation",
      startHere: "Start here",
      doThisFirst: "Do this first",
      mostPeopleNext: "Most people do this next",
      doThisWeek: "Do this week",
      doSoon: "Coming up",
      progressTitle: "Your Progress",
      progressDescription: "Track your journey",
      stepsCompleted: "steps completed",
      upgradeTitle: "Get your first 30 days done right",
      upgradeDescription: "Follow the right steps and make sure your documents are ready before you submit — no mistakes, no delays.",
      upgradeCta: "Unlock full access",
      upgradeFeatures: [
        "Know exactly what to do in your first 30 days",
        "Catch mistakes before submitting documents",
        "Follow the right steps in the right order"
      ],
      upgradeRefPrice: "$99",
      upgradePrice: "$49",
      upgradePriceLabel: "early access",
      upgradeOneTime: "One-time payment — no subscription",
      upgradeSupport: "Built to help you avoid delays and do things correctly the first time.",
      lockedTitle: "Premium content",
      lockedDescription: "Most newcomers miss steps — get a clear plan instead.",
      lockedButton: "Unlock full access",
      basicVersion: "You're viewing the basic version",
      basicVersionDescription: "Upgrade to see exactly what to do and avoid costly mistakes.",
      documentLocked: "Get your documents checked before submitting.",
      documentLockedCta: "Before submitting paperwork, we help you check for missing information.",
      lockedItems: {
        mistakes: "Common mistakes to avoid",
        nextSteps: "What to do next (step-by-step)",
        documents: "Documents you actually need"
      }
    }
  },
  fr: {
    // Navigation
    nav: {
      howItWorks: "Comment ça marche",
      pricing: "Tarifs",
      faq: "FAQ",
      login: "Connexion",
      getStarted: "Commencer",
      dashboard: "Tableau de bord",
      profile: "Profil",
      documents: "Documents",
      logout: "Déconnexion"
    },
    // Hero - First 30 days + mistake prevention
    hero: {
      headline: "Tout ce dont vous avez besoin pour vous installer au Canada dans vos 30 premiers jours, dans le bon ordre.",
      subheadline: "Des conseils étape par étape et des vérifications de documents pour éviter les erreurs, les délais et la confusion.",
      cta: "Obtenir mon plan étape par étape",
      ctaHint: "Accès complet au plan — aperçu gratuit inclus",
      tagline: "Évitez les erreurs. Soumettez en confiance."
    },
    // Trust signals
    trust: {
      helped: "documents vérifiés",
      rating: "confiance de soumission",
      bilingual: "100% bilingue"
    },
    // Problem section
    problem: {
      title: "Une petite erreur peut tout retarder de plusieurs semaines",
      subtitle: "Le temps de réaliser que quelque chose ne va pas, il est déjà trop tard.",
      items: [
        "« Ma demande a été rejetée pour une signature manquante... »",
        "« J'ai attendu 3 semaines pour apprendre que j'avais le mauvais formulaire... »",
        "« J'aurais aimé que quelqu'un vérifie mes documents avant. »"
      ]
    },
    // Common mistakes section
    commonMistakes: {
      wrongTitle: "La plupart des nouveaux arrivants se trompent",
      wrongItems: [
        "Ouvrir un compte bancaire avant d'avoir le NAS",
        "Oublier des documents requis → retards",
        "Faire les étapes dans le mauvais ordre",
        "Passer des heures à chercher des informations peu claires"
      ],
      result: "Résultat : des semaines perdues, des retards inutiles et une frustration évitable.",
      rightTitle: "Comment PaperPal vous aide à éviter ces erreurs",
      rightSubtitle: "Suivez les bonnes étapes dès le premier jour. Pas de confusion, pas de retards.",
      rightItems: [
        "Ordre clair (pas de devinettes)",
        "Documents exacts nécessaires",
        "Guidance étape par étape",
        "Tout au même endroit"
      ]
    },
    // Solution section
    solution: {
      title: "Soumettez en confiance",
      subtitle: "Sachez exactement ce qui manque avant d'envoyer",
      description: "Téléchargez vos documents et obtenez un retour instantané sur ce qui est complet, ce qu'il faut corriger, et si vous êtes prêt à soumettre."
    },
    // How it works
    howItWorks: {
      title: "Voici comment ça fonctionne",
      subtitle: "Trois étapes vers la confiance de soumission",
      steps: [
        { 
          title: "Parlez-nous de vous", 
          description: "Étudiant? Travailleur? Fraîchement arrivé? On vous pose quelques questions simples.",
          details: [
            "Indiquez votre statut (étudiant, travailleur, nouvel arrivant)",
            "Partagez votre date d'arrivée au Canada",
            "Précisez votre province",
            "Décrivez vos principales préoccupations"
          ]
        },
        { 
          title: "Téléchargez vos documents", 
          description: "Partagez vos formulaires, demandes ou pièces d'identité — on les vérifie instantanément.",
          details: [
            "Supporte PDF, images et documents numérisés",
            "Analyse IA pour la complétude",
            "Identifie les champs manquants et erreurs courantes",
            "Fonctionne avec les formulaires NAS, bancaires et d'immigration"
          ]
        },
        { 
          title: "Savez exactement si vous êtes prêt à soumettre", 
          description: "Voyez exactement ce qui est prêt, ce qu'il faut corriger, et votre niveau de préparation.",
          details: [
            "Statut clair prêt/pas prêt",
            "Problèmes spécifiques avec explications",
            "Confirmation de ce qui est correct",
            "Prochaines étapes après soumission"
          ]
        }
      ]
    },
    // What's included
    included: {
      title: "Tout pour soumettre correctement du premier coup",
      subtitle: "Évitez les délais et les rejets",
      items: [
        { title: "Vérification de complétude", description: "On détecte les champs, signatures et dates manquants" },
        { title: "Score de confiance de soumission", description: "Sachez si vous êtes prêt avant de soumettre" },
        { title: "Recommandations de correction", description: "Instructions claires sur ce qu'il faut corriger" },
        { title: "Détection des erreurs courantes", description: "On repère les erreurs que d'autres manquent" },
        { title: "Estimation des délais de traitement", description: "Sachez à quoi vous attendre après soumission" },
        { title: "Support bilingue complet", description: "Tout est disponible en français et en anglais" }
      ]
    },
    // Pricing
    pricing: {
      title: "Un prix. Tout inclus.",
      subtitle: "Pas d'abonnement, pas de frais cachés",
      planName: "Vos 30 premiers jours",
      price: "49 $",
      refPrice: "99 $",
      priceNote: "aujourd'hui",
      referralNote: "Payez seulement 39 $ avec un parrainage",
      earlyAccess: "Prix de lancement — augmentation prochaine",
      oneTime: "Paiement unique — accès instantané",
      features: [
        "Checklist personnalisée de 30 jours",
        "Guidance étape par étape FR/EN",
        "Révision de complétude des documents",
        "Erreurs courantes à éviter",
        "Support par courriel au besoin"
      ],
      cta: "Obtenir mon plan étape par étape",
      guarantee: "Pas satisfait ? On s'arrange. Sans poser de questions."
    },
    // Founder
    founder: {
      title: "Pourquoi j'ai créé ça",
      message: "Salut, je suis Nicolas. En aidant des amis avec leurs démarches canadiennes, j'ai vu à quel point c'était confus et stressant — même pour des gens brillants. Les sites gouvernementaux sont un labyrinthe. Les conseils en ligne se contredisent. Et rater une étape peut coûter des semaines.\n\nPaperPal vous offre un chemin clair, pour que vous ne perdiez pas de temps à tout comprendre seul."
    },
    // Testimonials
    testimonials: {
      title: "Ce que disent nos premiers utilisateurs"
    },
    // FAQ
    faq: {
      title: "Des questions ? On a les réponses.",
      viewAll: "Voir toutes les questions"
    },
    // CTA
    cta: {
      title: "Prêt à soumettre en confiance ?",
      subtitle: "Détectez les erreurs avant qu'elles ne causent des délais.",
      button: "Obtenir mon plan étape par étape",
      belowCta: "Prend moins de 2 minutes pour commencer"
    },
    // Footer
    footer: {
      tagline: "Vos 30 premiers jours au Canada, plus sereinement.",
      about: "À propos",
      contact: "Contact",
      disclaimer: "Avertissement",
      privacy: "Confidentialité",
      terms: "Conditions",
      disclaimerText: "PaperPal fournit de l'orientation et des explications à des fins éducatives. Nous ne sommes pas avocats, consultants en immigration ni représentants gouvernementaux."
    },
    // Auth
    auth: {
      login: "Bon retour parmi nous",
      signup: "Créez votre compte",
      email: "Courriel",
      password: "Mot de passe",
      firstName: "Prénom",
      lastName: "Nom",
      confirmPassword: "Confirmer le mot de passe",
      forgotPassword: "Mot de passe oublié ?",
      noAccount: "Première visite ?",
      hasAccount: "Déjà un compte ?",
      signupLink: "Créer un compte",
      loginLink: "Se connecter",
      orContinueWith: "ou",
      google: "Continuer avec Google"
    },
    // Onboarding
    onboarding: {
      title: "On vous prépare",
      step1: {
        title: "Quelle langue préférez-vous ?",
        subtitle: "Vous pouvez changer ça n'importe quand.",
        options: ["English", "Français"]
      },
      step2: {
        title: "Qu'est-ce qui vous amène au Canada ?",
        options: [
          { value: "international_student", label: "Je suis ici pour étudier" },
          { value: "worker", label: "Je suis ici pour travailler" },
          { value: "newcomer", label: "Je suis encore en train de voir" }
        ]
      },
      step3: {
        title: "Quand êtes-vous arrivé ?",
        subtitle: "Ça nous aide à prioriser vos tâches.",
        options: [
          { value: "not_arrived", label: "Je ne suis pas encore arrivé" },
          { value: "within_7_days", label: "Dans la dernière semaine" },
          { value: "1_4_weeks", label: "Il y a 1 à 4 semaines" },
          { value: "over_month", label: "Il y a plus d'un mois" }
        ]
      },
      step4: {
        title: "Dans quelle province êtes-vous ?",
        subtitle: "Votre plan est adapté à votre province. Chaque province a des étapes, des délais et des exigences différents. Nous ajustons tout pour que vous suiviez le bon processus dès le premier jour.",
        options: [
          { value: "ontario", label: "Ontario" },
          { value: "quebec", label: "Québec" },
          { value: "other", label: "Une autre province" }
        ],
        otherNote: "Nous allons quand même vous aider. Certains détails peuvent varier selon la province, et nous vous le signalerons."
      },
      step5: {
        title: "Pouvez-vous travailler au Canada ?",
        subtitle: "Ça affecte certaines priorités.",
        options: [
          { value: "yes", label: "Oui, je peux travailler" },
          { value: "not_yet", label: "Pas encore" },
          { value: "not_sure", label: "Je ne suis pas sûr" }
        ]
      },
      step6: {
        title: "Qu'est-ce qui vous stresse le plus ?",
        subtitle: "Choisissez tout ce qui s'applique — on va prioriser.",
        options: [
          { value: "sin", label: "Obtenir mon NAS" },
          { value: "bank", label: "Ouvrir un compte bancaire" },
          { value: "health", label: "L'assurance maladie" },
          { value: "taxes", label: "Comprendre les impôts" },
          { value: "forms", label: "Les formulaires travail/école" },
          { value: "unknown", label: "Je ne sais pas par où commencer" }
        ]
      },
      step7: {
        title: "Comment peut-on vous aider au mieux ?",
        options: [
          { value: "instructions", label: "Des instructions claires étape par étape" },
          { value: "explanations", label: "Juste m'expliquer ce que les choses veulent dire" },
          { value: "both", label: "Les deux, s'il vous plaît !" }
        ]
      },
      step8: {
        title: "Petite précision",
        notice: "PaperPal vous aide à comprendre quoi faire et dans quel ordre. Il est basé sur les exigences réelles au Canada et conçu pour vous aider à éviter les erreurs courantes. PaperPal fournit des conseils, pas des avis juridiques.",
        confirm: "Compris, on y va"
      },
      step9: {
        title: "On crée votre checklist...",
        subtitle: "Ça ne prend qu'un instant."
      },
      next: "Continuer",
      back: "Retour",
      submit: "Créer ma checklist"
    },
    // Dashboard
    dashboard: {
      welcome: "Bon retour",
      greeting: "Voici la suite de votre liste.",
      yourChecklist: "Vos 30 premiers jours",
      progress: "complété",
      nextStep: "Prochaine étape",
      sections: {
        day_1_3: "Semaine 1 : L'essentiel",
        day_4_7: "Semaine 1 : Argent et santé",
        day_8_14: "Semaine 2 : Configuration gouvernementale",
        day_15_30: "Semaines 3-4 : Travail et école",
        after_arrival: "Après votre arrivée"
      },
      status: {
        not_started: "À faire",
        in_progress: "En cours",
        completed: "Fait"
      },
      documents: {
        title: "Vérification de documents",
        description: "Téléchargez un formulaire et on vérifie qu'il est complet.",
        upload: "Télécharger un document"
      },
      help: {
        title: "Besoin d'aide ?",
        description: "On est là si vous êtes bloqué.",
        contact: "Nous contacter"
      }
    },
    // Checklist item
    checklistItem: {
      whatIs: "C'est quoi ?",
      whoNeeds: "Qui doit faire ça ?",
      when: "Quand le faire ?",
      whatToPrepare: "Quoi apporter ?",
      commonMistakes: "Erreurs courantes à éviter",
      nextStep: "Et après ?",
      notes: "Vos notes",
      notesPlaceholder: "Notez ce que vous voulez retenir...",
      markComplete: "Marquer comme fait",
      markInProgress: "Marquer comme en cours",
      markNotStarted: "Marquer comme à faire",
      back: "Retour à la checklist"
    },
    // Documents
    documents: {
      title: "Révision de documents",
      subtitle: "On vérifie que vos formulaires sont complets",
      upload: "Télécharger",
      dragDrop: "Déposez votre fichier ici, ou cliquez pour parcourir",
      allowedTypes: "PDF, JPG ou PNG — jusqu'à 10 Mo",
      review: "Réviser ce document",
      reviewing: "Révision en cours...",
      status: {
        pending: "En attente de révision",
        reviewed: "Révisé"
      },
      disclaimer: "On vérifie la complétude, pas la validité juridique. Vérifiez toujours avec les sources officielles."
    },
    // Profile
    profile: {
      title: "Votre profil",
      settings: "Préférences",
      language: "Langue",
      save: "Sauvegarder",
      saved: "Sauvegardé !"
    },
    // Contact
    contact: {
      title: "Nous contacter",
      subtitle: "On répond habituellement en un jour ou deux.",
      name: "Votre nom",
      email: "Courriel",
      message: "Comment peut-on vous aider ?",
      send: "Envoyer",
      sent: "Merci ! On vous répond bientôt."
    },
    // Legal
    legal: {
      title: "Avertissement important",
      content: [
        "PaperPal fournit de l'orientation et des explications à des fins éducatives.",
        "Nous ne sommes pas un cabinet d'avocats, un consultant en immigration ni une agence gouvernementale.",
        "Nous ne fournissons pas de conseils juridiques, d'immigration ou fiscaux.",
        "Les informations fournies peuvent ne pas être complètes, à jour ou exactes pour votre situation.",
        "Vérifiez toujours les informations auprès de sources gouvernementales officielles ou de professionnels agréés.",
        "En utilisant PaperPal, vous reconnaissez ces limitations."
      ]
    },
    // Common
    common: {
      loading: "Chargement...",
      error: "Quelque chose s'est mal passé",
      retry: "Réessayer",
      cancel: "Annuler",
      save: "Sauvegarder",
      delete: "Supprimer",
      edit: "Modifier",
      close: "Fermer"
    },
    // Premium / Freemium
    premium: {
      badge: "Premium",
      freeBadge: "Gratuit",
      yourPlan: "Votre plan personnel",
      planDescription: "Étapes prioritaires selon votre situation",
      startHere: "Commencez ici",
      doThisFirst: "Faites ça en premier",
      mostPeopleNext: "La plupart font ça ensuite",
      doThisWeek: "Cette semaine",
      doSoon: "À venir",
      progressTitle: "Votre progression",
      progressDescription: "Suivez votre parcours",
      stepsCompleted: "étapes terminées",
      upgradeTitle: "Réussissez vos 30 premiers jours",
      upgradeDescription: "Suivez les bonnes étapes et assurez-vous que vos documents sont prêts avant de les soumettre — sans erreurs, sans délais.",
      upgradeCta: "Débloquer l'accès complet",
      upgradeFeatures: [
        "Sachez exactement quoi faire dans vos 30 premiers jours",
        "Détectez les erreurs avant de soumettre vos documents",
        "Suivez les bonnes étapes dans le bon ordre"
      ],
      upgradeRefPrice: "99 $",
      upgradePrice: "49 $",
      upgradePriceLabel: "accès anticipé",
      upgradeOneTime: "Paiement unique — sans abonnement",
      upgradeSupport: "Conçu pour vous aider à éviter les délais et à faire les choses correctement du premier coup.",
      lockedTitle: "Contenu Premium",
      lockedDescription: "La plupart des nouveaux arrivants ratent des étapes — obtenez un plan clair.",
      lockedButton: "Débloquer l'accès complet",
      basicVersion: "Vous consultez la version de base",
      basicVersionDescription: "Passez à Premium pour savoir exactement quoi faire et éviter les erreurs coûteuses.",
      documentLocked: "Faites vérifier vos documents avant de les soumettre.",
      documentLockedCta: "Avant de soumettre vos papiers, on vous aide à vérifier s'il manque des infos.",
      lockedItems: {
        mistakes: "Erreurs courantes à éviter",
        nextSteps: "Quoi faire ensuite (étape par étape)",
        documents: "Documents dont vous avez vraiment besoin"
      }
    }
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('paperpal_language') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('paperpal_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key;
      }
    }
    
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
